import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Code, 
  ChevronDown, 
  ChevronUp, 
  Clock, 
  ArrowRight, 
  CheckCircle, 
  XCircle, 
  Copy,
  Trash2,
  Eye
} from 'lucide-react'
import ReactJsonView from 'react-json-view'

interface RequestHistoryItem {
  request: any
  response: any
  timestamp: string
}

interface Props {
  requestHistory: RequestHistoryItem[]
}

export default function DebugPanel({ requestHistory }: Props) {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())
  const [selectedTab, setSelectedTab] = useState<'requests' | 'schemas' | 'stats'>('requests')

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedItems(newExpanded)
  }

  const copyToClipboard = async (data: any) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2))
      // Could add a toast notification here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
    }
  }

  const stats = useMemo(() => {
    const methods = requestHistory.reduce((acc, item) => {
      const method = item.request.method
      acc[method] = (acc[method] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const avgResponseTime = requestHistory.length > 0 
      ? requestHistory.reduce((acc, item, index) => {
          const prevTime = index > 0 ? new Date(requestHistory[index - 1].timestamp).getTime() : 0
          const currentTime = new Date(item.timestamp).getTime()
          return acc + (currentTime - prevTime)
        }, 0) / requestHistory.length 
      : 0

    return {
      totalRequests: requestHistory.length,
      methods,
      avgResponseTime: Math.round(avgResponseTime),
      successRate: requestHistory.length > 0 
        ? Math.round((requestHistory.filter(item => !item.response.error).length / requestHistory.length) * 100)
        : 0
    }
  }, [requestHistory])

  const methodColors: Record<string, string> = {
    'dsap.discover': 'text-blue-600 bg-blue-100',
    'dsap.verifyAccess': 'text-green-600 bg-green-100',
    'dsap.logActivity': 'text-purple-600 bg-purple-100',
    'dsap.checkCompliance': 'text-orange-600 bg-orange-100',
    'dsap.classifyData': 'text-pink-600 bg-pink-100'
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-24">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2 mb-3">
          <Code className="h-5 w-5 text-dsap-600" />
          <h3 className="text-lg font-semibold text-gray-900">Debug Panel</h3>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-gray-500">Live</span>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex space-x-1">
          {[
            { id: 'requests', label: 'Requests', count: requestHistory.length },
            { id: 'schemas', label: 'Schemas', count: 0 },
            { id: 'stats', label: 'Stats', count: 0 }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id as any)}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                selectedTab === tab.id
                  ? 'bg-dsap-100 text-dsap-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 max-h-96 overflow-y-auto">
        <AnimatePresence mode="wait">
          {selectedTab === 'requests' && (
            <motion.div
              key="requests"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-3"
            >
              {requestHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Eye className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    No requests yet. Start by discovering the DSAP server.
                  </p>
                </div>
              ) : (
                requestHistory.map((item, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Request Header */}
                    <div className="p-3 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className={`px-2 py-1 rounded text-xs font-mono font-medium ${
                            methodColors[item.request.method] || 'text-gray-600 bg-gray-100'
                          }`}>
                            {item.request.method}
                          </span>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <Clock className="h-3 w-3" />
                            <span>{new Date(item.timestamp).toLocaleTimeString()}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {item.response.error ? (
                            <XCircle className="h-4 w-4 text-red-500" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                          
                          <button
                            onClick={() => copyToClipboard({ request: item.request, response: item.response })}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Copy to clipboard"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                          
                          <button
                            onClick={() => toggleExpanded(index)}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {expandedItems.has(index) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Collapsed View */}
                    {!expandedItems.has(index) && (
                      <div className="p-3">
                        <div className="flex items-center space-x-2 text-sm">
                          <span className="text-gray-600">ID:</span>
                          <span className="font-mono text-gray-900">{item.request.id}</span>
                          <ArrowRight className="h-3 w-3 text-gray-400" />
                          <span className={`font-medium ${
                            item.response.error ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {item.response.error ? 'ERROR' : 'SUCCESS'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Expanded View */}
                    <AnimatePresence>
                      {expandedItems.has(index) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="p-3 space-y-4">
                            {/* Request */}
                            <div>
                              <h5 className="text-sm font-medium text-gray-900 mb-2">
                                Request
                              </h5>
                              <div className="bg-gray-900 rounded overflow-hidden">
                                <ReactJsonView
                                  src={item.request}
                                  theme="monokai"
                                  collapsed={false}
                                  displayDataTypes={false}
                                  displayObjectSize={false}
                                  enableClipboard={false}
                                  name={false}
                                  style={{ fontSize: '11px' }}
                                />
                              </div>
                            </div>

                            {/* Response */}
                            <div>
                              <h5 className="text-sm font-medium text-gray-900 mb-2">
                                Response
                              </h5>
                              <div className="bg-gray-900 rounded overflow-hidden">
                                <ReactJsonView
                                  src={item.response}
                                  theme="monokai"
                                  collapsed={item.response.result ? 1 : false}
                                  displayDataTypes={false}
                                  displayObjectSize={false}
                                  enableClipboard={false}
                                  name={false}
                                  style={{ fontSize: '11px' }}
                                />
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))
              )}
            </motion.div>
          )}

          {selectedTab === 'schemas' && (
            <motion.div
              key="schemas"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div className="text-center py-8">
                <Code className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-2">
                  DSAP Protocol Schemas
                </p>
                <p className="text-xs text-gray-400">
                  All requests/responses are validated against JSON schemas
                </p>
              </div>
              
              <div className="space-y-2">
                {Object.keys(methodColors).map(method => (
                  <div key={method} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm font-mono">{method}</span>
                    <span className="text-xs text-gray-500">v1.0 schema</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {selectedTab === 'stats' && (
            <motion.div
              key="stats"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              {/* Overall Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded p-3 text-center">
                  <div className="text-lg font-semibold text-blue-900">
                    {stats.totalRequests}
                  </div>
                  <div className="text-xs text-blue-600">Total Requests</div>
                </div>
                <div className="bg-green-50 rounded p-3 text-center">
                  <div className="text-lg font-semibold text-green-900">
                    {stats.successRate}%
                  </div>
                  <div className="text-xs text-green-600">Success Rate</div>
                </div>
              </div>

              {/* Method Breakdown */}
              <div>
                <h5 className="text-sm font-medium text-gray-900 mb-2">
                  Methods Called
                </h5>
                <div className="space-y-1">
                  {Object.entries(stats.methods).map(([method, count]) => (
                    <div key={method} className="flex items-center justify-between py-1">
                      <span className="text-xs font-mono text-gray-600">
                        {method}
                      </span>
                      <span className="text-xs font-semibold text-gray-900">
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Performance */}
              <div>
                <h5 className="text-sm font-medium text-gray-900 mb-2">
                  Performance
                </h5>
                <div className="text-xs text-gray-600">
                  <div className="flex justify-between py-1">
                    <span>Avg Response Time:</span>
                    <span className="font-mono">{stats.avgResponseTime}ms</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span>Protocol Version:</span>
                    <span className="font-mono">DSAP 1.0</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span>Transport:</span>
                    <span className="font-mono">JSON-RPC 2.0</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-500 text-center">
          Debug mode shows real-time DSAP protocol interactions
        </p>
      </div>
    </div>
  )
}